import { Component, Input, OnChanges } from '@angular/core';
import Highcharts from 'highcharts';
import { Subscription } from 'rxjs/Subscription';

import { ScreenSizeService } from '../../shared/services/screensize.service';
import { Portfolio } from '../../shared/models/Recommendation/RecommendationInterfaces';

@Component({
  selector: 'md-asset-breakdown',
  template: require('./asset-breakdown.component.html'),
  styles: [require('./asset-breakdown.component.scss')]
})
export class AssetBreakdownComponent implements OnChanges {
  @Input() data: Portfolio;
  allocationChart;
  subscriptions$: Subscription = new Subscription();
  isMobile: boolean = false;
  chartProperties = {};

  constructor(screenSize: ScreenSizeService) {
    this.screenSize = screenSize;
  }

  ngOnInit() {
    this.setIsMobile(this.screenSize.getWindowSize().width);
    this.setChartProperties();
    this.subscriptions$.add(this.screenSize.resizeSubscription$.subscribe(x => {
      this.onResize(x.width);
    }));
  }

  ngAfterViewInit() {
    this.initializeChart();
    this.setChartData();
  }

  ngOnChanges() {
    this.setChartData();
  }

  setChartData() {
    if (this.data && this.allocationChart) {
      // Highcharts mutates the array. We need to make a copy.
      const clone = this.data.assetSets.map(x => ({ ...x }));
      this.allocationChart.series[0].setData(clone, true);
    }
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
    if (this.allocationChart) {
      this.allocationChart.destroy();
    }
  }

  initializeChart() {
    this.allocationChart = Highcharts.chart({
      credits: {
        enabled: false
      },
      chart: {
        id: 'pieChart',
        spacingTop: this.chartProperties.chart.spacingTop, // this.isMobile ? 0 : 15,
        spacingBottom: this.chartProperties.chart.spacingBottom, // this.isMobile ? 0 : 15,
        spacingLeft: 0,
        spacingRight: 0,
        margin: this.chartProperties.chart.margin,
        plotBackgroundColor: 'transparent',
        plotBorderWidth: 0,
        plotShadow: false,
        renderTo: 'container',

        backgroundColor: 'transparent',
        reflow: true
      },
      legend: {
        align: this.chartProperties.legend.align,
        verticalAlign: this.chartProperties.legend.verticalAlign,
        layout: this.chartProperties.legend.layout,
        alignColumns: true,
        x: this.chartProperties.legend.x,
        padding: 0,
        itemDistance: 10,
        itemMarginBottom: 5,
        itemMarginTop: 5,
        plotBackgroundColor: 'transparent',
        labelFormat: '{percentage:.0f}% {name}',
        borderRadius: 5,
        symbolHeight: 14,
        symbolWidth: 14,
        symbolRadius: 7, // needs to be half the height and width for round legend icons
        itemWidth: 140,
        floating: false,
        itemStyle: {
          lineHeight: '24px',
          fontSize: this.chartProperties.legend.itemStyle.fontSize
        }
      },
      title: {
        text: ''
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        series: {
          pointWidth: 180,
          borderWidth: 2,
          borderColor: '#FFFFFF'
        },
        pie: {
          align: this.chartProperties.plotOptions.pie.align,
          center: this.chartProperties.plotOptions.pie.center,
          dataLabels: {
            enabled: false
          },
          startAngle: -180,
          endAngle: 180,
          borderColor: null,
          size: '175',
          showInLegend: true,
          point: {
            events: {
              legendItemClick(e) {
                // prevent legend items from being clicked and omitted from the chart
                e.preventDefault();
              }
            }
          }
        }
      },
      series: [
        {
          type: 'pie',
          name: 'Total Value',
          innerSize: '72%',
          data: [],
          states: {
            hover: {}
          }
        }
      ]
    });
  }

  setIsMobile(width: number) {
    if (width <= 400) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  }

  onResize(width: number) {
    if (!this.allocationChart) {
      return;
    }

    this.setIsMobile(width);
    this.setChartProperties();

    this.allocationChart.update({
      chart: {
        spacingTop: this.chartProperties.chart.spacingTop,
        spacingBottom: this.chartProperties.chart.spacingBottom,
        margin: this.chartProperties.chart.margin
      },
      legend: {
        align: this.chartProperties.legend.align,
        verticalAlign: this.chartProperties.legend.verticalAlign,
        layout: this.chartProperties.legend.layout,
        x: this.chartProperties.legend.x,
        itemStyle: {
          fontSize: this.chartProperties.legend.itemStyle.fontSize
        }
      },
      plotOptions: {
        pie: {
          align: this.chartProperties.plotOptions.pie.align,
          center: this.chartProperties.plotOptions.pie.center
        }
      }
    }, true, true, false);

    this.allocationChart.reflow();
  }

  setChartProperties() {
    this.chartProperties = {
      chart: {
        spacingTop: this.isMobile ? 0 : 15,
        spacingBottom: this.isMobile ? 0 : 15,
        margin: this.isMobile ? [0, 0, 0, 0] : [0, 0, 0, -21]
      },
      legend: {
        align: this.isMobile ? 'center' : 'right',
        verticalAlign: this.isMobile ? 'bottom' : 'middle',
        layout: this.isMobile ? 'horizontal' : 'vertical',
        x: this.isMobile ? -10 : -15,
        itemStyle: {
          fontSize: this.isMobile ? '14px' : '16px'
        }
      },
      plotOptions: {
        pie: {
          align: this.isMobile ? 'center' : 'left',
          center: this.isMobile ? ['50%', '30%'] : ['27%', '50%']
        }
      }
    };
  }
}
